import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Pencil, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function fmtInr(n) {
  return `₹ ${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch { return iso; }
}

export default function VoucherView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [v, setV] = useState(null);

  useEffect(() => {
    api.get(`/voucher/vouchers/${id}`).then((r) => setV(r.data));
  }, [id]);

  const openPdf = async () => {
    try {
      const r = await api.get(`/voucher/vouchers/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Mobile-friendly: use <a download> instead of window.open for better compatibility
      const a = document.createElement("a");
      a.href = url;
      a.download = `${v.voucher_no}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 200);
    } catch { toast.error("Could not open PDF"); }
  };

  const openKycPdf = async (salesmanId) => {
    try {
      const r = await api.get(`/voucher/salesmen/${salesmanId}/kyc-pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kyc-${salesmanId}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 200);
    } catch { toast.error("Could not open KYC PDF"); }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this voucher permanently?")) return;
    try {
      await api.delete(`/voucher/vouchers/${id}`);
      toast.success("Voucher deleted");
      nav("/voucher/vouchers");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  if (!v) return <div className="text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav("/voucher/vouchers")}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <div className="overline text-zinc-500">Voucher</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1" data-testid="voucher-view-title">{v.voucher_no}</h1>
            <div className="text-xs text-zinc-500 mt-1">
              <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${v.voucher_type === "travelling" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                {v.voucher_type === "travelling" ? "Travelling Voucher" : "Delivery Challan"}
              </span>
              <span className="ml-2">· Created {fmtDate(v.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={openPdf} className="bg-zinc-900 hover:bg-zinc-800" data-testid="download-pdf-button">
            <Download className="w-4 h-4 mr-1.5" /> View PDF
          </Button>
          <Link to={`/voucher/vouchers/${id}/edit`}>
            <Button variant="outline" data-testid="edit-voucher-button"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
          </Link>
          {isAdmin && (
            <Button variant="ghost" className="text-red-600 hover:text-red-700" onClick={onDelete} data-testid="delete-voucher-button">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="overline text-zinc-500 mb-2">From (Company)</div>
          <div className="font-heading font-bold text-lg">{v.company_snapshot?.company_name || "Om Jewellers"}</div>
          <div className="text-sm text-zinc-600 whitespace-pre-line">{v.company_snapshot?.address}</div>
          <div className="text-xs text-zinc-500 mt-2">
            {v.company_snapshot?.gst_number && <>GSTIN: {v.company_snapshot.gst_number}<br /></>}
            {v.company_snapshot?.mobile && <>Mobile: {v.company_snapshot.mobile} · </>}
            {v.company_snapshot?.email}
          </div>
        </Card>
        <Card className="p-6">
          <div className="overline text-zinc-500 mb-2">To (Customer)</div>
          <div className="font-heading font-bold text-lg">{v.to_customer_name || "—"}</div>
          <div className="text-sm text-zinc-600 whitespace-pre-line">{v.to_customer_address}</div>
          {v.to_customer_gst && <div className="text-xs text-zinc-500 mt-2">GSTIN: {v.to_customer_gst}</div>}
        </Card>
      </div>

      <Card className="p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div><div className="overline text-zinc-500">Voucher Date</div><div className="font-medium">{fmtDate(v.voucher_date)}</div></div>
          {v.voucher_type === "travelling" ? (
            <>
              <div><div className="overline text-zinc-500">Valid From</div><div className="font-medium">{fmtDate(v.valid_from)}</div></div>
              <div><div className="overline text-zinc-500">Valid To</div><div className="font-medium">{fmtDate(v.valid_to)}</div></div>
            </>
          ) : (
            <>
              <div><div className="overline text-zinc-500">Delivery Date</div><div className="font-medium">{fmtDate(v.delivery_date)}</div></div>
              <div><div className="overline text-zinc-500">Delivery Location</div><div className="font-medium">{v.delivery_location || "—"}</div></div>
            </>
          )}
          <div><div className="overline text-zinc-500">Vehicle No.</div><div className="font-medium">{v.vehicle_number || "—"}</div></div>
          <div><div className="overline text-zinc-500">Transport</div><div className="font-medium">{v.transport || "—"}</div></div>
          <div><div className="overline text-zinc-500">Salesman</div><div className="font-medium">{v.salesman_name || "—"}</div></div>
          {v.voucher_type === "travelling" && (
            <div className="col-span-2 sm:col-span-4"><div className="overline text-zinc-500">Travelling Persons</div><div className="font-medium whitespace-pre-line">{v.travelling_persons || "—"}</div></div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-heading font-bold text-lg mb-3">Items</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm hidden md:table" data-testid="voucher-items-table">
            <thead className="hidden md:table-header-group">
              <tr className="text-left text-zinc-500 border-b border-border">
                <th className="pb-2 overline">#</th>
                <th className="pb-2 overline">Item</th>
                <th className="pb-2 overline">HSN</th>
                <th className="pb-2 overline text-right">Gross</th>
                <th className="pb-2 overline text-right">Net</th>
                <th className="pb-2 overline text-right">Rate</th>
                <th className="pb-2 overline text-right">Amount</th>
                <th className="pb-2 overline text-right">GST%</th>
                <th className="pb-2 overline text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {v.items?.map((it, idx) => (
                <tr key={idx}>
                  <td className="py-2 text-zinc-500">{idx + 1}</td>
                  <td className="py-2 font-medium">{it.item_name}</td>
                  <td className="py-2">{it.hsn || "—"}</td>
                  <td className="py-2 text-right num">{Number(it.gross_weight || 0).toFixed(3)}</td>
                  <td className="py-2 text-right num">{Number(it.net_weight || 0).toFixed(3)}</td>
                  <td className="py-2 text-right num">{fmtInr(it.gold_rate)}</td>
                  <td className="py-2 text-right num">{fmtInr(it.net_amount)}</td>
                  <td className="py-2 text-right num">{Number(it.gst_percent || 0).toFixed(2)}</td>
                  <td className="py-2 text-right num font-semibold">{fmtInr(it.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile card view for items */}
          <div className="md:hidden space-y-3 mt-3" data-testid="mobile-voucher-items">
            {v.items?.map((it, idx) => (
              <Card key={idx} className="p-3" data-testid={`mobile-voucher-item-${idx}`}>
                <div className="font-medium">{idx + 1}. {it.item_name}</div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-1">
                  <div><span className="text-zinc-500">HSN:</span> <span className="num">{it.hsn || "—"}</span></div>
                  <div><span className="text-zinc-500">Gross Wt:</span> <span className="num">{Number(it.gross_weight || 0).toFixed(3)} g</span></div>
                  <div><span className="text-zinc-500">Net Wt:</span> <span className="num">{Number(it.net_weight || 0).toFixed(3)} g</span></div>
                  <div><span className="text-zinc-500">Rate/g:</span> <span className="num">{fmtInr(it.gold_rate)}</span></div>
                  <div><span className="text-zinc-500">Amount:</span> <span className="num">{fmtInr(it.net_amount)}</span></div>
                  <div><span className="text-zinc-500">GST%:</span> <span className="num">{Number(it.gst_percent || 0).toFixed(2)}%</span></div>
                  <div className="col-span-2"><span className="text-zinc-500">Total:</span> <span className="num font-semibold">{fmtInr(it.total_amount)}</span></div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-4 mt-4 border-t border-border">
          <div><div className="overline text-zinc-500">Gross Wt</div><div className="num font-semibold">{Number(v.total_gross_weight || 0).toFixed(3)} g</div></div>
          <div><div className="overline text-zinc-500">Net Wt</div><div className="num font-semibold">{Number(v.total_net_weight || 0).toFixed(3)} g</div></div>
          <div><div className="overline text-zinc-500">Subtotal</div><div className="num font-semibold">{fmtInr(v.subtotal)}</div></div>
          <div><div className="overline text-zinc-500">GST</div><div className="num font-semibold">{fmtInr(v.gst_amount)}</div></div>
          <div><div className="overline text-zinc-500">Grand Total</div><div className="num font-black text-lg" data-testid="grand-total">{fmtInr(v.grand_total)}</div></div>
        </div>
      </Card>

      {v.remarks && (
        <Card className="p-6">
          <div className="overline text-zinc-500 mb-2">Remarks</div>
          <div className="text-sm whitespace-pre-line">{v.remarks}</div>
        </Card>
      )}
    </div>
  );
}
